// 1.5.44 — the passkey's name follows the wallet's name (Alexander, 26 Oct, HL-18; "yes" 29 Oct).
//
// A passkey's label is fixed when it is made (*M® · Lucy*), and WebAuthn has no rename. What
// exists since 2025 is the Signal API: a site may tell the password manager the current name of
// one of its passkeys — `PublicKeyCredential.signalCurrentUserDetails({ rpId, userId, name,
// displayName })` — and iCloud Keychain (Safari / iOS 26) and Google Password Manager (Chrome)
// update the entry. It needs the passkey's user handle, which M keeps with the credential: from
// creation since 1.5.44, and for an older passkey from its next login (a discoverable passkey
// answers every login with its handle).
//
// Nothing here is a secret or a ceremony: no prompt, no Face ID, no server. A browser without the
// API, or a passkey whose handle isn't known yet, simply keeps the old label — the name sheet
// then says where to rename it by hand, as before.
//
// Told once per name per passkey (`m.pkName`: credential id → the label told), and again whenever
// the wallet's name differs — on a rename, and at the login after a rename made on another device.

import { readCredential } from "./credentialStore";
import { passkeyLabel } from "./passkeyClient";
import { recordError, recordMeasure } from "./diag";

const TOLD = "m.pkName";

type Signal = (o: { rpId: string; userId: string; name: string; displayName: string }) => Promise<void>;

function signalFn(): Signal | null {
  const P = (globalThis as { PublicKeyCredential?: { signalCurrentUserDetails?: Signal } }).PublicKeyCredential;
  return typeof P?.signalCurrentUserDetails === "function" ? P.signalCurrentUserDetails.bind(P) : null;
}

function told(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(TOLD) ?? "{}") as Record<string, string>; } catch { return {}; }
}

/** This browser can rename the passkey of `address` (the wallet it is bound to). */
export function canRenamePasskey(address: string): boolean {
  const c = readCredential();
  return !!signalFn() && !!c?.userHandle && !!c.address && c.address.toLowerCase() === address.toLowerCase();
}

/**
 * Tell the password manager the passkey's name for `name`. Resolves true when it was told now or
 * before (the same label), false when it can't be (no API, no handle, another wallet, no name, or
 * the browser refused). Never throws.
 */
export async function tellPasskeyName(address: string, name: string | null | undefined): Promise<boolean> {
  const n = (name ?? "").replace(/\s+/g, " ").trim();
  const fn = signalFn();
  const c = readCredential();
  if (!n || !fn || !c?.userHandle || !canRenamePasskey(address)) return false;
  const label = passkeyLabel("", n);
  const all = told();
  if (all[c.id] === label) return true;
  try {
    await fn({ rpId: c.rpId || location.hostname, userId: c.userHandle, name: label, displayName: label });
    all[c.id] = label;
    try { localStorage.setItem(TOLD, JSON.stringify(all)); } catch { /* told again next time: harmless */ }
    recordMeasure("passkey name", `told · ${label}`);
    return true;
  } catch (e) {
    recordError("passkey/name", e);
    return false;
  }
}
